import { useMemo, useRef } from "preact/hooks";
import { Fragment } from "preact/jsx-runtime";
import { Combobox, Transition } from "@headlessui/react";
import { useSignal } from "@preact/signals";
import { cva } from "class-variance-authority";
import type { AutocompleteProps, BaseChoice } from "./interfaces";
import CloseIcon from "@material-design-icons/svg/outlined/close.svg";
import cn from "classnames";

const DEFAULT_MAX_SUGGESTIONS = 10;

export const inputVariants = cva("w-full", {
    variants: {
        variant: {
            compact: "rounded-md border border-gray-300 px-4 outline-none",
            flat: "px-2 outline-none h-full",
            classic:
                "rounded-3xl border border-gray-300 px-4 py-1 outline-none transition duration-100 focus:ring-2 focus:ring-sky-500 focus:ring-offset-2 focus:ring-offset-white",
        },
    },
    defaultVariants: {
        variant: "compact",
    },
});

const inputOpenVariants = cva("", {
    variants: {
        variant: {
            compact: "outline-b-none mb-[1px] rounded-b-none border-b-0",
            flat: "",
            classic: "",
        },
    },
    defaultVariants: {
        variant: "compact",
    },
});

export const optionsVariants = cva(
    "overflow-y-overlay absolute z-30 max-h-60 w-full bg-white",
    {
        variants: {
            variant: {
                compact:
                    "-mt-[1px] rounded-b-md border border-t-0 border-gray-300 shadow-lg",
                flat: "-mt-[1px] rounded-b-md border border-t-0 border-gray-300 shadow-lg",
                classic:
                    "rounded-lg border border-gray-100 shadow-lg focus:outline-sky-500",
            },
            direction: {
                down: "top-full mt-1",
                up: "bottom-full mb-1",
            },
        },
        defaultVariants: {
            variant: "compact",
            direction: "down",
        },
    },
);

const optionVariant = cva(
    "cursor-pointer truncate aria-disabled:cursor-default",
    {
        variants: {
            variant: {
                compact: "py-1/2 border-t border-gray-200 px-4",
                flat: "py-1/2 border-t border-gray-200 px-4",
                classic: "px-4 py-1",
            },
        },
        defaultVariants: {
            variant: "compact",
        },
    },
);

const Autocomplete = <Choice extends BaseChoice>({
    value,
    onChange,
    complete,
    placeholder,
    className,
    renderLabel = (choice) => choice?.id.toString() || "",
    renderTooltip,
    renderOption = (choice) => <span>{choice.id}</span>,
    onMore,
    spellcheck,
    disabled,
    noInsertSelected,
    noQuickSearch,
    variant,
    direction,
    label,
    maxSuggestions = DEFAULT_MAX_SUGGESTIONS,
    inputClassName,
    clear = true,
}: AutocompleteProps<Choice>) => {
    const query = useSignal("");
    const choices = useMemo(
        () => complete(query.value),
        [complete, query.value],
    );
    const buttonRef = useRef<HTMLButtonElement>(null);
    const finalChoices = useMemo(
        () =>
            // this is needed to prevent an issue when the value is not in the choices
            // and opening the dropdown with an empty query gets the first value selected
            // which will lead to the value being overwritten by the first choice when closing the dropdown
            noInsertSelected
                ? choices
                : query.value === "" && value && !choices.includes(value)
                  ? [value, ...choices]
                  : choices,
        [choices, noInsertSelected, value, query.value],
    );
    const finalActiveChoices = useMemo(
        () => finalChoices.filter((i) => !i.disabled),
        [finalChoices],
    );
    const hasOverflow =
        finalActiveChoices.length > maxSuggestions && query.value;
    const overflowCount = hasOverflow
        ? finalActiveChoices.length - maxSuggestions
        : 0;

    return (
        <div
            className={cn(
                "group relative",
                label && "flex flex-col",
                variant === "flat" && "h-full",
                className,
            )}
        >
            {typeof label === "string" ? (
                <span className="text-sm text-gray-500">{label}</span>
            ) : (
                label
            )}
            <Combobox value={value} onChange={onChange} disabled={disabled}>
                <Combobox.Button ref={buttonRef} className="hidden" />
                <Combobox.Input
                    title={
                        value && renderTooltip
                            ? renderTooltip(value)
                            : undefined
                    }
                    data-autocomplete-input
                    autoComplete="off"
                    spellcheck={spellcheck}
                    placeholder={placeholder}
                    readOnly={noQuickSearch}
                    className={({ open }) =>
                        cn(
                            inputVariants({
                                variant,
                                className: inputClassName,
                            }),
                            open && inputOpenVariants({ variant }),
                            disabled && "bg-gray-100",
                        )
                    }
                    displayValue={(item: Choice | null) => renderLabel(item)}
                    onClick={() => {
                        buttonRef.current?.click();
                    }}
                    onChange={(event) =>
                        (query.value = event.currentTarget.value)
                    }
                    onKeyDown={(event) => {
                        if (event.key === "Escape" && !query.peek()) {
                            onChange(null);
                        }
                    }}
                />
                {clear && !disabled && (
                    <button
                        type="button"
                        title="Clear"
                        className={cn(
                            "absolute right-2 flex items-center rounded-full fill-current",
                            "text-gray-500 transition-all duration-100 hover:bg-gray-100 hover:text-gray-700",
                            "invisible opacity-0 top-1/2 -translate-y-1/2",
                            variant === "classic" ? "p-1" : "p-0.5",
                            value &&
                                "group-focus-within:visible group-focus-within:opacity-100",
                        )}
                        onClick={() => {
                            query.value = "";
                            onChange(null);
                        }}
                    >
                        <CloseIcon width="18" height="18" />
                    </button>
                )}
                <Transition
                    as={Fragment}
                    enterFrom="opacity-0 translate-y-2"
                    enter="transition easy-in-out duration-200"
                    leave="transition easy-in duration-100"
                    leaveTo="opacity-0 translate-y-2"
                    afterLeave={() => (query.value = "")}
                >
                    <Combobox.Options
                        className={optionsVariants({ variant, direction })}
                    >
                        {finalChoices.length === 0 && query.value && (
                            <div className="flex h-full w-full select-none items-center justify-center border-t border-gray-200 py-4 text-gray-400">
                                Nothing found
                            </div>
                        )}
                        {finalChoices.slice(0, maxSuggestions).map((choice) => (
                            <Combobox.Option
                                key={choice.id}
                                value={choice}
                                disabled={choice.disabled}
                                className={({ active }: { active: boolean }) =>
                                    cn(
                                        optionVariant({ variant }),
                                        choice.disabled && "bg-gray-100",
                                        active && "bg-sky-100",
                                    )
                                }
                            >
                                {renderOption(choice)}
                            </Combobox.Option>
                        ))}
                        {hasOverflow && (
                            <button
                                type="Button"
                                onClick={() => onMore?.(query.value)}
                                className="flex w-full select-none items-center justify-center border-t border-gray-200 bg-gray-100 py-3 hover:bg-gray-200 hover:text-sky-500"
                            >
                                We found {overflowCount} more.{" "}
                                {onMore && "Click to see all"}
                            </button>
                        )}
                    </Combobox.Options>
                </Transition>
            </Combobox>
        </div>
    );
};

export default Autocomplete;
